/**
 * Asset Detail Page.
 *
 * Displays full details for a single asset with tabbed sections:
 * Details, Costs, and Barcode. Supports inline editing of status
 * and other fields.
 */

import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Trash2,
  FlaskConical,
  MapPin,
  ShieldAlert,
  ShieldOff,
  MoveRight,
  Wrench,
  Sparkles,
  Package,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import CalcTrigger from "@/components/ui/CalcTrigger";
import PaymentPanel from "@/features/payments/PaymentPanel";
import {
  useAsset,
  useUpdateAsset,
  useCreateWorkOrder,
  useAssetWorkOrders,
} from "@/features/inventory/api";
import AssetLocationPicker from "@/components/ui/AssetLocationPicker";
import DetailCard from "@/components/ui/DetailCard";
import type { DetailField } from "@/components/ui/DetailCard";
import CategorySelect from "@/components/ui/CategorySelect";
import { toast } from "@/stores/toastStore";
import ActionDrawer from "@/components/ui/ActionDrawer";
import AssetStatusBadge from "@/features/inventory/components/AssetStatusBadge";
import StatusPicker from "@/features/inventory/components/StatusPicker";
import Badge from "@/components/ui/Badge";
import CostBreakdown from "@/features/inventory/components/CostBreakdown";
import BarcodeDisplay from "@/features/inventory/components/BarcodeDisplay";
import EntityFormsPanel from "@/features/forms/components/EntityFormsPanel";
import FileUpload from "@/components/ui/FileUpload";
import PhotoGallery from "@/components/ui/PhotoGallery";
import {
  useAssetListings,
  useListAsset,
  useDelistAsset,
} from "@/features/integrations/api";
import { useIntegrations } from "@/features/integrations/api";
import { usePricingRecommendation } from "@/features/insights/api";
import { useEntityTimeline } from "@/features/activity/api";
import TimelineView from "@/features/activity/components/TimelineView";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import LocationComboSelect from "@/components/ui/LocationComboSelect";
import ConfirmDeleteDialog from "@/components/ui/ConfirmDeleteDialog";

type Tab =
  | "details"
  | "costs"
  | "work_orders"
  | "forms"
  | "documents"
  | "listings"
  | "activity"
  | "status_history"
  | "barcode";

function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: asset, isLoading, isError } = useAsset(id);
  const updateMutation = useUpdateAsset(id || "");
  const { data: woData } = useAssetWorkOrders(id);
  const { data: linkedPO } = useQuery<{
    po_id: string;
    po_number: string;
    line_id: string;
    line_status: string;
    line_total: string;
    vendor_name: string | null;
    order_date: string | null;
    status: string;
  } | null>({
    queryKey: ["asset-po", id],
    queryFn: async () => {
      const resp = await apiClient.get(`/api/v1/assets/${id}/purchase-order`);
      return resp.data || null;
    },
    enabled: !!id,
  });

  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<Tab>("details");
  const [statusHistory, setStatusHistory] = useState<
    Array<{
      from_status: string;
      to_status: string;
      trigger: string;
      trigger_event: string | null;
      reason: string | null;
      created_at: string;
    }>
  >([]);
  const [showHoldForm, setShowHoldForm] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [holdRelease, setHoldRelease] = useState("");
  const [showMoveForm, setShowMoveForm] = useState(false);
  const [moveLocation, setMoveLocation] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCreateWO, setShowCreateWO] = useState(false);
  const [woTitle, setWoTitle] = useState("");
  const [woType, setWoType] = useState("repair");
  const [woPriority, setWoPriority] = useState("medium");
  const [woDesc, setWoDesc] = useState("");
  const [woHours, setWoHours] = useState("");
  const createWoMutation = useCreateWorkOrder();

  const receiveMutation = useMutation({
    mutationFn: async () => {
      if (!linkedPO) throw new Error("No linked PO");
      await apiClient.post(
        `/api/v1/purchase-orders/${linkedPO.po_id}/receive-lines`,
        { line_ids: [linkedPO.line_id] },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets", id] });
      qc.invalidateQueries({ queryKey: ["asset", id] });
      qc.invalidateQueries({ queryKey: ["asset-po", id] });
      toast.success(
        "Item received",
        `${asset?.name} has been marked as received.`,
      );
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error("Failed to receive item", msg || "Please try again.");
    },
  });

  const placeHoldMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post(`/api/v1/assets/${id}/hold`, {
          reason: holdReason,
          expected_release: holdRelease
            ? new Date(holdRelease).toISOString()
            : undefined,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets", id] });
      setShowHoldForm(false);
      setHoldReason("");
      setHoldRelease("");
      toast.success("Hold placed");
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error("Failed to place hold", msg || "Please try again.");
    },
  });

  const releaseHoldMutation = useMutation({
    mutationFn: async () =>
      (await apiClient.post(`/api/v1/assets/${id}/release-hold`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets", id] });
      toast.success("Hold released");
    },
    onError: () => toast.error("Failed to release hold"),
  });

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);

  const loadStatusHistory = async () => {
    if (!id) return;
    try {
      const resp = await apiClient.get(`/api/v1/assets/${id}/status-history`);
      setStatusHistory(resp.data as typeof statusHistory);
    } catch {
      /* ignore */
    }
  };

  const handleDelete = () => {
    if (!id) return;
    setShowDeleteDialog(true);
  };

  // Parse asset name into year/make/model components.
  const TWO_WORD_MAKES = new Set([
    "john deere",
    "case ih",
    "new holland",
    "massey ferguson",
    "agco allis",
    "land rover",
    "can am",
    "arctic cat",
    "bad boy",
    "club car",
  ]);

  const parseAndPopulate = async () => {
    if (!asset) return;
    const parts = asset.name.trim().split(/\s+/);
    let idx = 0;
    let year: string | undefined;
    let make: string | undefined;
    let model: string | undefined;

    // Find year anywhere in the string.
    const yearIdx = parts.findIndex((p) => /^(19|20)\d{2}$/.test(p));
    if (yearIdx >= 0) {
      year = parts[yearIdx];
      parts.splice(yearIdx, 1);
    }

    // Detect make (including two-word makes).
    if (parts[idx]) {
      const twoWord = `${parts[idx]} ${parts[idx + 1] || ""}`.toLowerCase();
      if (TWO_WORD_MAKES.has(twoWord)) {
        make = `${parts[idx]} ${parts[idx + 1]}`;
        idx += 2;
      } else {
        make = parts[idx];
        idx++;
      }
    }

    // Remaining is model.
    if (idx < parts.length) {
      model = parts.slice(idx).join(" ");
    }

    // Only update fields that are currently empty.
    const updates: Record<string, unknown> = {};
    if (year && !asset.year) updates.year = parseInt(year);
    if (make && !asset.make) updates.make = make;
    if (model && !asset.model) updates.model = model;

    if (Object.keys(updates).length === 0) {
      toast.info(
        "No empty fields to populate",
        "Year, make, and model are already set.",
      );
      return;
    }

    await updateMutation.mutateAsync(updates);
    toast.success(
      "Fields populated from name",
      `Updated: ${Object.keys(updates).join(", ")}`,
    );
  };

  const composeNameFromFields = async () => {
    if (!asset) return;
    const nameParts = [
      asset.year ? String(asset.year) : null,
      asset.make,
      asset.model,
      asset.variant,
      asset.category !== "general" ? asset.category : null,
    ].filter(Boolean);
    if (nameParts.length === 0) {
      toast.info(
        "No fields to compose from",
        "Set year, make, or model first.",
      );
      return;
    }
    const newName = nameParts.join(" ");
    if (newName === asset.name) {
      toast.info(
        "Name already matches",
        "The name already follows the convention.",
      );
      return;
    }
    await updateMutation.mutateAsync({ name: newName } as Record<
      string,
      unknown
    >);
    toast.success("Name updated", newName);
  };

  if (isLoading) return <LoadingSpinner className="py-24" size="lg" />;
  if (isError || !asset) {
    return (
      <div className="py-24 text-center">
        <p className="text-[var(--text-secondary)]">Asset not found.</p>
        <Button
          variant="secondary"
          className="mt-4"
          onClick={() => navigate("/inventory")}
        >
          Back to Inventory
        </Button>
      </div>
    );
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: "details", label: "Details" },
    { key: "costs", label: "Costs" },
    { key: "work_orders", label: "Work Orders" },
    { key: "forms", label: "Forms" },
    { key: "documents", label: "Photos & Docs" },
    { key: "listings", label: "Listings" },
    { key: "activity", label: "Activity" },
    { key: "status_history", label: "Status History" },
    { key: "barcode", label: "Barcode / QR" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate("/inventory")}
            className="mt-1 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-[var(--text-primary)]">
                {asset.name}
              </h2>
              <StatusPicker assetId={asset.id} status={asset.status} />
              {/* `status_is_terminal` comes from this tenant's own lifecycle.
                  This used to read `asset.status !== "Sold"`, which is wrong
                  for a renamed lifecycle and for the five other final
                  statuses — a written-off machine kept accruing days in
                  stock. */}
              {!asset.status_is_terminal && (
                <Badge variant="default">
                  {asset.days_in_inventory}d in inventory
                </Badge>
              )}
            </div>
            <p className="mt-1 font-mono text-sm text-[var(--text-secondary)]">
              {asset.sku}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {asset.status === "On Order" &&
            linkedPO &&
            linkedPO.line_status !== "received" && (
              <Button
                size="sm"
                onClick={() => receiveMutation.mutate()}
                isLoading={receiveMutation.isPending}
              >
                <Package className="h-4 w-4" />
                Receive
              </Button>
            )}
          {asset.is_on_hold ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => releaseHoldMutation.mutate()}
              isLoading={releaseHoldMutation.isPending}
            >
              <ShieldOff className="h-4 w-4" />
              Release hold
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowHoldForm(!showHoldForm)}
            >
              <ShieldAlert className="h-4 w-4" />
              {showHoldForm ? "Cancel" : "Place hold"}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowMoveForm(!showMoveForm);
              setMoveLocation(asset.location || "");
            }}
          >
            <MoveRight className="h-4 w-4" />
            {showMoveForm ? "Cancel Move" : "Move"}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/reports?tab=what-if`)}
          >
            <FlaskConical className="h-4 w-4" />
            Simulate Sale
          </Button>
          {asset.yard_section_id && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/inventory?tab=yard-map`)}
            >
              <MapPin className="h-4 w-4" />
              Facility Map
            </Button>
          )}
          <Button variant="danger" size="sm" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      {/* Hold banner */}
      {asset.is_on_hold && (
        <div className="flex items-center gap-3 rounded-[var(--radius-lg)] border border-[var(--text-warning)]/30 bg-[var(--fill-warning)] px-4 py-3">
          <ShieldAlert className="h-5 w-5 text-[var(--text-warning)] shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-[var(--text-warning)]">
              This asset is on hold
            </p>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">
              {asset.hold_reason}
              {asset.hold_placed_at && (
                <span className="ml-2 text-[var(--text-tertiary)]">
                  · Since {new Date(asset.hold_placed_at).toLocaleDateString()}
                </span>
              )}
              {asset.hold_expected_release && (
                <span className="ml-2 text-[var(--text-tertiary)]">
                  · Expected release:{" "}
                  {new Date(asset.hold_expected_release).toLocaleDateString()}
                </span>
              )}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => releaseHoldMutation.mutate()}
            isLoading={releaseHoldMutation.isPending}
          >
            Release
          </Button>
        </div>
      )}

      {/* Place hold drawer */}
      <ActionDrawer
        isOpen={showHoldForm && !asset.is_on_hold}
        onClose={() => setShowHoldForm(false)}
        title="Place Hold"
        description={`Reserve ${asset.name} and prevent changes.`}
        icon={ShieldAlert}
        size="sm"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            placeHoldMutation.mutate();
          }}
          className="space-y-4"
        >
          <Input
            label="Reason for hold"
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
            required
            placeholder="e.g., Pending buyer financing approval, Awaiting title transfer"
          />
          <Input
            label="Expected release date (optional)"
            type="date"
            value={holdRelease}
            onChange={(e) => setHoldRelease(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowHoldForm(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              isLoading={placeHoldMutation.isPending}
            >
              Place Hold
            </Button>
          </div>
        </form>
      </ActionDrawer>

      {/* Move Asset Drawer */}
      <ActionDrawer
        isOpen={showMoveForm}
        onClose={() => setShowMoveForm(false)}
        title="Move Asset"
        description={`Change the location of ${asset.name}.`}
        icon={MoveRight}
        size="sm"
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await updateMutation.mutateAsync({
                location: moveLocation || undefined,
              });
              setShowMoveForm(false);
              toast.success(
                "Asset moved",
                moveLocation ? `Moved to ${moveLocation}` : "Location cleared",
              );
            } catch {
              toast.error("Failed to move asset");
            }
          }}
          className="space-y-4"
        >
          <div className="rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3 text-xs text-[var(--text-secondary)]">
            Current location:{" "}
            <span className="font-medium text-[var(--text-primary)]">
              {asset.location || "Not set"}
            </span>
          </div>

          <LocationComboSelect
            label="New Location"
            value={moveLocation}
            onChange={setMoveLocation}
            placeholder="Search or type a location..."
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowMoveForm(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              isLoading={updateMutation.isPending}
            >
              <MoveRight className="h-3.5 w-3.5" /> Move
            </Button>
          </div>
        </form>
      </ActionDrawer>

      {/* Create Work Order Drawer */}
      <ActionDrawer
        isOpen={showCreateWO}
        onClose={() => setShowCreateWO(false)}
        title="Create Work Order"
        description={`Create a work order for ${asset.name}.`}
        icon={Wrench}
        size="md"
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              const result = (await createWoMutation.mutateAsync({
                asset_id: id,
                title: woTitle,
                work_type: woType,
                priority: woPriority,
                description: woDesc || undefined,
                estimated_hours: woHours ? parseFloat(woHours) : undefined,
              })) as Record<string, unknown>;
              setShowCreateWO(false);
              setWoTitle("");
              setWoDesc("");
              setWoHours("");
              toast.success(
                "Work order created",
                `${result.wo_number as string} created.`,
              );
            } catch {
              toast.error("Failed to create work order");
            }
          }}
          className="space-y-4"
        >
          <Input
            label="Title"
            value={woTitle}
            onChange={(e) => setWoTitle(e.target.value)}
            required
            placeholder="e.g., Oil change and filter replacement"
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                Work Type
              </label>
              <select
                value={woType}
                onChange={(e) => setWoType(e.target.value)}
                className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
              >
                {["repair", "maintenance", "inspection", "prep", "custom"].map(
                  (t) => (
                    <option key={t} value={t}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </option>
                  ),
                )}
              </select>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                Priority
              </label>
              <select
                value={woPriority}
                onChange={(e) => setWoPriority(e.target.value)}
                className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
              >
                {["low", "medium", "high", "urgent"].map((p) => (
                  <option key={p} value={p}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <Input
            label="Estimated Hours"
            type="number"
            value={woHours}
            onChange={(e) => setWoHours(e.target.value)}
            placeholder="Optional"
          />
          <div>
            <label className="block text-[13px] font-medium text-[var(--text-primary)]">
              Description
            </label>
            <textarea
              value={woDesc}
              onChange={(e) => setWoDesc(e.target.value)}
              placeholder="Describe the work to be performed..."
              rows={3}
              className="mt-1.5 block w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowCreateWO(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              isLoading={createWoMutation.isPending}
            >
              <Wrench className="h-3.5 w-3.5" /> Create
            </Button>
          </div>
        </form>
      </ActionDrawer>

      {id && (
        <ConfirmDeleteDialog
          isOpen={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          apiPath="/api/v1/assets"
          entityId={id}
          queryKey="assets"
          onDeleted={() => navigate("/inventory")}
        />
      )}

      {/* Tabs */}
      <div className="border-b border-[var(--border-default)]">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? "border-[var(--color-interactive-primary)] text-[var(--color-interactive-primary)]"
                  : "border-transparent text-[var(--text-secondary)] hover:border-[var(--border-strong)] hover:text-[var(--text-secondary)]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "details" && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* Basic Information */}
          <DetailCard
            title="Basic Information"
            headerAction={
              <button
                type="button"
                onClick={parseAndPopulate}
                className="flex items-center gap-1 text-[10px] font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-300 px-2 py-1 rounded-[var(--radius-sm)] hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
                title="Parse name into Year, Make, Model fields"
              >
                <Sparkles className="h-3 w-3" />
                Name → Fields
              </button>
            }
            fields={
              [
                {
                  key: "name",
                  label: "Name",
                  value: asset.name,
                  placeholder: "e.g., 2025 Ford F150",
                  onSave: async (v) => {
                    await updateMutation.mutateAsync({ name: v } as Record<
                      string,
                      unknown
                    >);
                    toast.success("Name updated");
                  },
                },
                {
                  key: "description",
                  label: "Description",
                  value: asset.description,
                  type: "textarea",
                  placeholder: "Add a description...",
                  onSave: async (v) => {
                    await updateMutation.mutateAsync({
                      description: v || undefined,
                    } as Record<string, unknown>);
                    toast.success("Description updated");
                  },
                },
                {
                  key: "category",
                  label: "Category",
                  value: asset.category,
                  editable: false,
                  render: () => (
                    <CategorySelect
                      value={asset.category}
                      onChange={async (v) => {
                        await updateMutation.mutateAsync({
                          category: v,
                        } as Record<string, unknown>);
                        toast.success("Category updated");
                      }}
                    />
                  ),
                },
                {
                  key: "status",
                  label: "Status",
                  value: asset.status,
                  editable: false,
                  render: () => <AssetStatusBadge status={asset.status} />,
                },
                {
                  key: "location",
                  label: "Location",
                  value: asset.location,
                  editable: false,
                  render: () => (
                    <AssetLocationPicker
                      assetId={asset.id}
                      currentLocation={asset.location}
                      currentYardSectionId={asset.yard_section_id}
                      currentYardLocationId={asset.yard_location_id}
                      onLocationChanged={() =>
                        qc.invalidateQueries({
                          queryKey: ["asset", id],
                        })
                      }
                    />
                  ),
                },
              ] satisfies DetailField[]
            }
          />

          {/* Equipment Details */}
          <DetailCard
            title="Equipment Details"
            headerAction={
              <button
                type="button"
                onClick={composeNameFromFields}
                className="flex items-center gap-1 text-[10px] font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)] px-2 py-1 rounded-[var(--radius-sm)] hover:bg-[var(--surface-sunken)] transition-colors"
                title="Compose name from Year, Make, Model, Category"
              >
                Fields → Name
              </button>
            }
            fields={
              [
                {
                  key: "year",
                  label: "Year",
                  value: asset.year,
                  type: "number",
                  placeholder: "e.g., 2025",
                  validate: (v) =>
                    v && (Number(v) < 1900 || Number(v) > 2099)
                      ? "Year must be 1900–2099"
                      : null,
                  onSave: async (v) => {
                    await updateMutation.mutateAsync({
                      year: v ? Number(v) : undefined,
                    } as Record<string, unknown>);
                    toast.success("Year updated");
                  },
                },
                {
                  key: "make",
                  label: "Make",
                  value: asset.make,
                  placeholder: "e.g., Ford, John Deere",
                  onSave: async (v) => {
                    await updateMutation.mutateAsync({
                      make: v || undefined,
                    } as Record<string, unknown>);
                    toast.success("Make updated");
                  },
                },
                {
                  key: "model",
                  label: "Model",
                  value: asset.model,
                  placeholder: "e.g., F150, 6120M",
                  onSave: async (v) => {
                    await updateMutation.mutateAsync({
                      model: v || undefined,
                    } as Record<string, unknown>);
                    toast.success("Model updated");
                  },
                },
                {
                  key: "variant",
                  label: "Variant",
                  value: asset.variant,
                  placeholder: "e.g., Premium, MFWD",
                  onSave: async (v) => {
                    await updateMutation.mutateAsync({
                      variant: v || undefined,
                    } as Record<string, unknown>);
                    toast.success("Variant updated");
                  },
                },
                {
                  key: "serial_number",
                  label: "Serial Number",
                  value: asset.serial_number,
                  placeholder: "Enter serial number",
                  onSave: async (v) => {
                    await updateMutation.mutateAsync({
                      serial_number: v || undefined,
                    } as Record<string, unknown>);
                    toast.success("Serial number updated");
                  },
                },
                {
                  key: "vin",
                  label: "VIN",
                  value: asset.vin,
                  placeholder: "17-character VIN",
                  onSave: async (v) => {
                    await updateMutation.mutateAsync({
                      vin: v || undefined,
                    } as Record<string, unknown>);
                    toast.success("VIN updated");
                  },
                },
                {
                  key: "lot_number",
                  label: "Lot Number",
                  value: asset.lot_number,
                  placeholder: "Enter lot number",
                  onSave: async (v) => {
                    await updateMutation.mutateAsync({
                      lot_number: v || undefined,
                    } as Record<string, unknown>);
                    toast.success("Lot number updated");
                  },
                },
              ] satisfies DetailField[]
            }
          />

          {/* Procurement */}
          <DetailCard
            title="Procurement"
            className="lg:col-span-2"
            fields={
              [
                {
                  key: "po_number",
                  label: "PO Number",
                  value: linkedPO?.po_number || null,
                  editable: false,
                  render: () =>
                    linkedPO ? (
                      <Link
                        to={`/purchase-orders/${linkedPO.po_id}`}
                        className="text-sm font-mono text-brand-600 hover:text-brand-700 hover:underline"
                      >
                        {linkedPO.po_number}
                      </Link>
                    ) : (
                      <span className="text-sm text-[var(--text-tertiary)]">
                        —
                      </span>
                    ),
                },
                {
                  key: "vendor_name",
                  label: "Vendor",
                  value: asset.vendor_name || linkedPO?.vendor_name || null,
                  placeholder: "Enter vendor name",
                  onSave: async (v) => {
                    await updateMutation.mutateAsync({
                      vendor_name: v || undefined,
                    } as Record<string, unknown>);
                    toast.success("Vendor updated");
                  },
                },
                {
                  key: "purchase_date",
                  label: "Purchase Date",
                  value: asset.purchase_date
                    ? new Date(asset.purchase_date).toLocaleDateString()
                    : linkedPO?.order_date
                      ? new Date(linkedPO.order_date).toLocaleDateString()
                      : null,
                  type: "text",
                  placeholder: "MM/DD/YYYY",
                  onSave: async (v) => {
                    const parsed = v ? new Date(v).toISOString() : undefined;
                    await updateMutation.mutateAsync({
                      purchase_date: parsed,
                    } as Record<string, unknown>);
                    toast.success("Purchase date updated");
                  },
                },
                {
                  key: "purchase_price",
                  label: "Purchase Price",
                  value: asset.purchase_price,
                  editable: false,
                  render: (v) => (
                    <span className="text-sm font-medium text-[var(--text-primary)]">
                      {formatCurrency(v as number)}
                    </span>
                  ),
                },
                {
                  key: "acquisition_notes",
                  label: "Notes",
                  value: asset.acquisition_notes,
                  type: "textarea",
                  placeholder: "Purchase or acquisition notes",
                  onSave: async (v) => {
                    await updateMutation.mutateAsync({
                      acquisition_notes: v || undefined,
                    } as Record<string, unknown>);
                    toast.success("Notes updated");
                  },
                },
              ] satisfies DetailField[]
            }
          />

          {/* Sale Details */}
          <DetailCard
            title="Sale"
            className="lg:col-span-2"
            fields={
              [
                {
                  key: "sale_order_number",
                  label: "SO Number",
                  value: asset.sale_order_number || null,
                  editable: !asset.sale_order_id,
                  placeholder: "Enter sale order number",
                  onSave: !asset.sale_order_id
                    ? async (v) => {
                        await updateMutation.mutateAsync({
                          sale_order_number: v || undefined,
                        } as Record<string, unknown>);
                        toast.success("Sale order number updated");
                      }
                    : undefined,
                },
                {
                  key: "purchase_order_number",
                  label: "PO Reference",
                  value: asset.purchase_order_number || null,
                  editable: !asset.purchase_order_id,
                  placeholder: "Enter PO number",
                  onSave: !asset.purchase_order_id
                    ? async (v) => {
                        await updateMutation.mutateAsync({
                          purchase_order_number: v || undefined,
                        } as Record<string, unknown>);
                        toast.success("PO number updated");
                      }
                    : undefined,
                },
                {
                  key: "sale_price",
                  label: "Sale Price",
                  value: asset.sale_price,
                  type: "text",
                  placeholder: "0.00",
                  render: (v) =>
                    v != null ? (
                      <span className="text-sm font-medium text-[var(--text-primary)]">
                        {formatCurrency(v as number)}
                      </span>
                    ) : null,
                  onSave: async (v) => {
                    await updateMutation.mutateAsync({
                      sale_price: v ? parseFloat(String(v)) : undefined,
                    } as Record<string, unknown>);
                    toast.success("Sale price updated");
                  },
                },
                {
                  key: "sale_date",
                  label: "Sale Date",
                  value: asset.sale_date
                    ? new Date(asset.sale_date).toLocaleDateString()
                    : null,
                  type: "text",
                  placeholder: "MM/DD/YYYY",
                  onSave: async (v) => {
                    const parsed = v ? new Date(v).toISOString() : undefined;
                    await updateMutation.mutateAsync({
                      sale_date: parsed,
                    } as Record<string, unknown>);
                    toast.success("Sale date updated");
                  },
                },
                {
                  key: "sale_notes",
                  label: "Sale Notes",
                  value: asset.sale_notes,
                  type: "textarea",
                  placeholder: "Notes about the sale",
                  onSave: async (v) => {
                    await updateMutation.mutateAsync({
                      sale_notes: v || undefined,
                    } as Record<string, unknown>);
                    toast.success("Sale notes updated");
                  },
                },
              ] satisfies DetailField[]
            }
          />

          {/* Financial Summary */}
          <Card className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-[var(--text-primary)]">
                Financial summary
              </h3>
              <div className="flex gap-1">
                <CalcTrigger
                  size="sm"
                  mode="margin"
                  label="Margin"
                  prefill={{ cost: String(asset.total_cogs) }}
                  source={asset.sku}
                />
                <CalcTrigger
                  size="sm"
                  mode="profit"
                  label="Profit"
                  prefill={{
                    purchaseCost: String(asset.purchase_price),
                    reconCost: String(
                      Number(asset.total_cogs) - Number(asset.landed_cost),
                    ),
                    shippingCost: String(
                      Number(asset.landed_cost) - Number(asset.purchase_price),
                    ),
                  }}
                  source={asset.sku}
                />
                <CalcTrigger
                  size="sm"
                  mode="roi"
                  label="ROI"
                  prefill={{ invested: String(asset.total_cogs) }}
                  source={asset.sku}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-[var(--text-secondary)]">
                  Purchase price
                </p>
                <p className="text-lg font-bold text-[var(--text-primary)]">
                  {formatCurrency(asset.purchase_price)}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--text-secondary)]">
                  Landed cost
                </p>
                <p className="text-lg font-bold text-[var(--text-primary)]">
                  {formatCurrency(asset.landed_cost)}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--text-secondary)]">
                  Total COGS
                </p>
                <p className="text-lg font-bold text-[var(--color-interactive-primary)]">
                  {formatCurrency(asset.total_cogs)}
                </p>
              </div>
            </div>
          </Card>

          {/* Payments — show when asset has a linked PO */}
          {linkedPO && (
            <Card className="lg:col-span-2">
              <div className="mb-4">
                <h3 className="text-sm font-medium text-[var(--text-primary)]">
                  Payments
                </h3>
                <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">
                  Record payments for this asset or the full purchase order{" "}
                  <Link
                    to={`/purchase-orders/${linkedPO.po_id}`}
                    className="text-brand-600 hover:underline"
                  >
                    {linkedPO.po_number}
                  </Link>
                </p>
              </div>
              <div className="space-y-4">
                <PaymentPanel
                  entityType="purchase_order"
                  entityId={linkedPO.po_id}
                  lineItems={[
                    {
                      id: linkedPO.line_id,
                      asset_name: asset.name,
                      description: asset.name,
                      total_price:
                        Number(linkedPO.line_total) || asset.purchase_price,
                      line_total:
                        Number(linkedPO.line_total) || asset.purchase_price,
                    },
                  ]}
                />
              </div>
            </Card>
          )}

          {/* Pricing Recommendation */}
          <PricingCard assetId={asset.id} />
        </div>
      )}

      {activeTab === "costs" && (
        <CostBreakdown
          assetId={asset.id}
          landedCost={asset.landed_cost}
          totalCogs={asset.total_cogs}
        />
      )}

      {activeTab === "work_orders" && (
        <Card padding="none">
          <div className="flex items-center justify-between border-b border-[var(--border-default)] px-4 py-3">
            <div>
              <h3 className="text-sm font-medium text-[var(--text-primary)]">
                Work Orders
              </h3>
              <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
                Maintenance, repairs, and service history for this asset.
              </p>
            </div>
            <Button size="sm" onClick={() => setShowCreateWO(true)}>
              <Wrench className="h-3.5 w-3.5" /> Create Work Order
            </Button>
          </div>
          {(() => {
            const wos = woData?.items || [];
            if (wos.length === 0) {
              return (
                <div className="px-4 py-8 text-center">
                  <Wrench className="h-8 w-8 text-[var(--text-tertiary)] mx-auto" />
                  <p className="mt-2 text-sm text-[var(--text-secondary)]">
                    No work orders for this asset yet.
                  </p>
                  <p className="text-xs text-[var(--text-tertiary)] mt-1">
                    Create a work order to track maintenance and repairs.
                  </p>
                </div>
              );
            }
            return (
              <div className="divide-y divide-[var(--border-subtle)]">
                {wos.map((wo) => (
                  <Link
                    key={wo.id}
                    to={`/shop/${wo.id}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--surface-sunken)] transition-colors"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-300">
                      <Wrench className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-semibold text-[var(--text-primary)]">
                          {wo.wo_number}
                        </span>
                        <Badge
                          variant={
                            wo.status === "Completed"
                              ? "success"
                              : wo.status === "In Progress"
                                ? "warning"
                                : wo.status === "Open"
                                  ? "info"
                                  : "default"
                          }
                        >
                          {wo.status}
                        </Badge>
                        <Badge variant="default">{wo.work_type}</Badge>
                      </div>
                      <p className="text-xs text-[var(--text-secondary)] truncate mt-0.5">
                        {wo.title}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      {wo.total_cost > 0 && (
                        <p className="text-sm font-semibold text-[var(--text-primary)]">
                          ${wo.total_cost.toLocaleString()}
                        </p>
                      )}
                      <p className="text-[10px] text-[var(--text-tertiary)]">
                        {new Date(wo.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            );
          })()}
        </Card>
      )}

      {activeTab === "forms" && (
        <EntityFormsPanel entityType="asset" entityId={asset.id} />
      )}

      {activeTab === "documents" && (
        <div className="space-y-6">
          <PhotoGallery
            entityType="asset"
            entityId={asset.id}
            maxImages={3}
            primaryImageId={
              asset.images && asset.images.length > 0
                ? (asset.images[0] as string)
                : null
            }
            onSetPrimary={async (attachmentId) => {
              const currentImages = (asset.images as string[]) || [];
              const reordered = [
                attachmentId,
                ...currentImages.filter((id) => id !== attachmentId),
              ];
              await updateMutation.mutateAsync({
                images: reordered,
              } as Record<string, unknown>);
              toast.success("Primary image set");
            }}
            onPhotoUploaded={async (attachmentId) => {
              const currentImages = (asset.images as string[]) || [];
              if (!currentImages.includes(attachmentId)) {
                await updateMutation.mutateAsync({
                  images: [...currentImages, attachmentId],
                } as Record<string, unknown>);
              }
            }}
            onPhotoDeleted={async (attachmentId) => {
              const currentImages = (asset.images as string[]) || [];
              await updateMutation.mutateAsync({
                images: currentImages.filter((id) => id !== attachmentId),
              } as Record<string, unknown>);
            }}
          />
          <Card>
            <h3 className="mb-3 text-sm font-medium text-[var(--text-primary)]">
              Other Documents
            </h3>
            <FileUpload entityType="asset" entityId={asset.id} />
          </Card>
        </div>
      )}

      {activeTab === "listings" && <ListingsPanel assetId={asset.id} />}

      {activeTab === "activity" && <AssetTimeline assetId={asset.id} />}

      {activeTab === "status_history" && (
        <StatusHistoryPanel
          assetId={asset.id}
          history={statusHistory}
          onLoad={loadStatusHistory}
        />
      )}

      {activeTab === "barcode" && (
        <div className="max-w-sm">
          <BarcodeDisplay assetId={asset.id} sku={asset.sku} />
        </div>
      )}
    </div>
  );
}

function AssetTimeline({ assetId }: { assetId: string }) {
  const { data } = useEntityTimeline("asset", assetId);
  return (
    <Card padding="sm">
      <TimelineView entries={data?.items || []} />
    </Card>
  );
}

function PricingCard({ assetId }: { assetId: string }) {
  const { data: pricing } = usePricingRecommendation(assetId);
  if (!pricing || !pricing.suggested_price) return null;

  const fmtC = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(v);

  return (
    <Card className="lg:col-span-2 border-[var(--text-success)]/30 bg-[var(--fill-success)]">
      <h3 className="mb-2 text-sm font-medium text-green-900">
        Suggested Price
      </h3>
      <div className="flex items-baseline gap-4">
        <p className="text-2xl font-bold text-[var(--text-success)]">
          {fmtC(pricing.suggested_price)}
        </p>
        <p className="text-xs text-[var(--text-success)]">
          {pricing.avg_margin_pct}% avg margin &middot;{" "}
          {pricing.comparable_sales_count} comparable sales
        </p>
      </div>
      <p className="mt-1 text-xs text-[var(--text-success)]">
        {pricing.method}
      </p>
    </Card>
  );
}

function ListingsPanel({ assetId }: { assetId: string }) {
  const { data: listings, isLoading } = useAssetListings(assetId);
  const { data: integrations } = useIntegrations();
  const listMutation = useListAsset(assetId);
  const delistMutation = useDelistAsset(assetId);

  const [showList, setShowList] = useState(false);
  const [selectedIntegId, setSelectedIntegId] = useState("");
  const [salePrice, setSalePrice] = useState("");

  const handleList = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedIntegId) return;
    try {
      await listMutation.mutateAsync({
        integration_id: selectedIntegId,
        sale_price: salePrice ? parseFloat(salePrice) : undefined,
      });
      setShowList(false);
      setSelectedIntegId("");
      setSalePrice("");
      toast.success("Asset listed on platform");
    } catch {
      toast.error("Failed to list asset");
    }
  };

  const statusColor: Record<string, string> = {
    active: "text-[var(--text-success)]",
    sold: "text-[var(--color-interactive-primary)]",
    delisted: "text-[var(--text-tertiary)]",
    error: "text-[var(--text-danger)]",
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-[var(--text-primary)]">
          Platform Listings
        </h3>
        <Button size="sm" onClick={() => setShowList(!showList)}>
          {showList ? "Cancel" : "List on Platform"}
        </Button>
      </div>

      {showList && (
        <Card>
          <form
            onSubmit={handleList}
            className="flex flex-wrap items-end gap-3"
          >
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)]">
                Platform
              </label>
              <select
                value={selectedIntegId}
                onChange={(e) => setSelectedIntegId(e.target.value)}
                required
                className="mt-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-sm"
              >
                <option value="">Select...</option>
                {integrations
                  ?.filter((i) => i.is_active)
                  .map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name} ({i.platform})
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)]">
                Sale Price
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                placeholder="Optional"
                className="mt-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-sm"
              />
            </div>
            <Button type="submit" size="sm" isLoading={listMutation.isPending}>
              List
            </Button>
          </form>
        </Card>
      )}

      {isLoading ? (
        <LoadingSpinner />
      ) : listings && listings.length > 0 ? (
        <Card padding="none">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-[var(--surface-sunken)] text-left text-xs font-medium text-[var(--text-secondary)]">
                <th className="px-4 py-2">Platform</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">URL</th>
                <th className="px-4 py-2">Listed</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {listings.map((l) => (
                <tr
                  key={l.id}
                  className="border-b border-[var(--border-subtle)]"
                >
                  <td className="px-4 py-2 capitalize font-medium">
                    {l.platform}
                  </td>
                  <td
                    className={`px-4 py-2 font-medium capitalize ${statusColor[l.status] || ""}`}
                  >
                    {l.status}
                  </td>
                  <td className="px-4 py-2">
                    {l.listing_url ? (
                      <a
                        href={l.listing_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--color-interactive-primary)] hover:underline text-xs"
                      >
                        {l.listing_url.slice(0, 40)}...
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-[var(--text-secondary)]">
                    {new Date(l.listed_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">
                    {l.status === "active" && (
                      <button
                        onClick={() =>
                          delistMutation.mutate(l.id, {
                            onSuccess: () => toast.success("Asset delisted"),
                            onError: () =>
                              toast.error("Failed to delist asset"),
                          })
                        }
                        className="text-xs text-[var(--text-danger)] hover:underline"
                      >
                        Delist
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : (
        <p className="text-sm text-[var(--text-tertiary)]">
          No listings yet. List this asset on a connected platform.
        </p>
      )}
    </div>
  );
}

function StatusHistoryPanel({
  assetId,
  history,
  onLoad,
}: {
  assetId: string;
  history: Array<{
    from_status: string;
    to_status: string;
    trigger: string;
    trigger_event: string | null;
    reason: string | null;
    created_at: string;
  }>;
  onLoad: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const { data: woData } = useAssetWorkOrders(assetId);

  if (!loaded) {
    onLoad();
    setLoaded(true);
  }

  const triggerLabel: Record<string, string> = {
    system: "System",
    manual: "Manual",
    automation: "Automation",
    workflow: "Workflow",
  };

  // Build unified timeline events from status transitions + work orders.
  interface TimelineEvent {
    type: "status" | "work_order";
    date: string;
    data: Record<string, unknown>;
  }

  const events: TimelineEvent[] = [];

  for (const h of history) {
    events.push({
      type: "status",
      date: h.created_at,
      data: h as unknown as Record<string, unknown>,
    });
  }

  for (const wo of woData?.items || []) {
    events.push({
      type: "work_order",
      date: wo.created_at,
      data: wo as unknown as Record<string, unknown>,
    });
  }

  // Sort newest first.
  events.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return (
    <Card padding="none">
      <div className="px-4 py-3 border-b border-[var(--border-default)]">
        <h3 className="text-sm font-medium text-[var(--text-primary)]">
          Asset History
        </h3>
        <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
          Status changes, work orders, and maintenance history.
        </p>
      </div>
      {events.length > 0 ? (
        <div className="divide-y divide-[var(--border-subtle)]">
          {events.map((event, i) => (
            <div key={i} className="px-4 py-3 flex items-start gap-3">
              {event.type === "status" ? (
                <>
                  <div className="flex-shrink-0 mt-0.5">
                    <div
                      className={`h-2.5 w-2.5 rounded-full ${
                        (event.data.trigger as string) === "system"
                          ? "bg-blue-500"
                          : (event.data.trigger as string) === "manual"
                            ? "bg-amber-500"
                            : (event.data.trigger as string) === "automation"
                              ? "bg-violet-500"
                              : "bg-slate-400"
                      }`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <AssetStatusBadge
                        status={event.data.from_status as string}
                      />
                      <span className="text-xs text-[var(--text-tertiary)]">
                        →
                      </span>
                      <AssetStatusBadge
                        status={event.data.to_status as string}
                      />
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          (event.data.trigger as string) === "system"
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                            : (event.data.trigger as string) === "manual"
                              ? "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
                              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                        }`}
                      >
                        {triggerLabel[event.data.trigger as string] ||
                          (event.data.trigger as string)}
                      </span>
                      {!!event.data.trigger_event && (
                        <span className="text-[10px] text-[var(--text-tertiary)]">
                          {String(event.data.trigger_event).replace(/_/g, " ")}
                        </span>
                      )}
                    </div>
                    {!!event.data.reason && (
                      <p className="text-xs text-[var(--text-secondary)] mt-1">
                        {event.data.reason as string}
                      </p>
                    )}
                    <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
                      {new Date(event.date).toLocaleString()}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/50">
                      <Wrench className="h-2.5 w-2.5 text-amber-600 dark:text-amber-300" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/shop/${event.data.id as string}`}
                        className="text-xs font-mono font-semibold text-brand-600 hover:underline"
                      >
                        {event.data.wo_number as string}
                      </Link>
                      <Badge
                        variant={
                          (event.data.status as string) === "Completed"
                            ? "success"
                            : (event.data.status as string) === "In Progress"
                              ? "warning"
                              : "default"
                        }
                      >
                        {event.data.status as string}
                      </Badge>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                        {(event.data.work_type as string) || "Work Order"}
                      </span>
                    </div>
                    <p className="text-xs text-[var(--text-primary)] mt-0.5">
                      {event.data.title as string}
                    </p>
                    {(event.data.total_cost as number) > 0 && (
                      <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">
                        Total cost: $
                        {(event.data.total_cost as number).toLocaleString()}
                      </p>
                    )}
                    <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
                      {new Date(event.date).toLocaleString()}
                    </p>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">
          No history recorded yet.
        </p>
      )}
    </Card>
  );
}

export default AssetDetailPage;
